import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Stack,
} from '@mui/material';
import { AppButton } from '../../../components/design-system';
import { RestartAlt as ResetIcon } from '@mui/icons-material';
import { opsDevicesService } from '../../../services/OpsDevicesService';

interface ResetDeviceModalProps {
  open: boolean;
  serialNumber: string;
  onClose: () => void;
  onSuccess: (serialNumber: string) => void;
}

const CHECKLIST = [
  'Physical device has been recovered and is in the warehouse',
  'Old customer data has been cleared from local storage on the device',
  'Device has been factory-reset to the standard image',
] as const;

export default function ResetDeviceModal({
  open,
  serialNumber,
  onClose,
  onSuccess,
}: ResetDeviceModalProps) {
  const [checks, setChecks] = useState([false, false, false]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allChecked = checks.every(Boolean);

  const handleClose = () => {
    setChecks([false, false, false]);
    setError(null);
    onClose();
  };

  const handleToggle = (index: number) => {
    setChecks((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!allChecked) {
      setError('Please confirm all checklist items.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await opsDevicesService.resetDevice(serialNumber);
      onSuccess(serialNumber);
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ResetIcon color="primary" />
        Reset Device for Redeployment
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Serial:</strong> {serialNumber}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This clears tenant assignment, claim/shipment metadata, provisioning artifacts, and the
          registered <strong>AK certificate</strong>, then sets inventory status to{' '}
          <strong>In Stock</strong>. Use after decommission or return when the device is ready to be
          reallocated.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Before resetting, confirm:
        </Typography>
        <Stack spacing={0.5}>
          {CHECKLIST.map((item, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={checks[index]}
                  onChange={() => handleToggle(index)}
                />
              }
              label={item}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <AppButton onClick={handleClose} disabled={submitting}>
          Cancel
        </AppButton>
        <AppButton
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !allChecked}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Reset Device
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}
